import { clearReferralNotices, syncReferralAccount } from "@/lib/referrals/attribution"
import { clearBrowseSnapshots } from "@/platform/app-mode/browse-history"
import { resetBridgeEvents } from "@/platform/bridge/document-events"
import { clearSignupState } from "@/platform/browser/signup-state"
import { setDatadogSessionUser } from "@/platform/observability/datadog/context"
import { currentViewer, isProbeKey } from "@/platform/session/current-viewer"
import { ANONYMOUS_VIEWER, isPrivateQuery, viewerOf } from "@/platform/session/viewer"
import { crossViewerBoundary } from "@/platform/session/viewer-boundary"
import { clearLastCheckoutProduct } from "@/platform/shopper/carts.client"
import { getStoredWishlist, resetWishlistOwnership, setStoredWishlist } from "@/platform/shopper/use-wishlist-id"

import type { Query, QueryClient } from "@tanstack/react-query"

const belongsToAnother =
  (viewer: string) =>
  (query: Query): boolean =>
    isPrivateQuery(query) && query.meta?.viewer !== viewer

const clearLocalSelections = (previous: string): void => {
  resetWishlistOwnership()
  if (getStoredWishlist()?.claimed) setStoredWishlist(null)
  if (previous === ANONYMOUS_VIEWER) return
  resetBridgeEvents()
  clearReferralNotices()
  clearLastCheckoutProduct()
  clearSignupState()
}

const bindViewer = (viewer: string): void => {
  setDatadogSessionUser(viewer === ANONYMOUS_VIEWER ? null : viewer)
  syncReferralAccount(viewer)
}

const retireViewer = (queryClient: QueryClient, previous: string, next: string): void => {
  crossViewerBoundary()
  clearBrowseSnapshots()
  const filters = { predicate: belongsToAnother(next) }
  void queryClient.cancelQueries(filters)
  queryClient.removeQueries(filters)
  clearLocalSelections(previous)
  bindViewer(next)
}

export const watchViewer = (queryClient: QueryClient): (() => void) => {
  let current = currentViewer(queryClient)
  if (current) bindViewer(current)

  return queryClient.getQueryCache().subscribe((event) => {
    if (!isProbeKey(event.query.queryKey)) return
    const data = event.query.state.data as Parameters<typeof viewerOf>[0] | undefined
    const next = data ? viewerOf(data) : null
    if (next === null || next === current) return
    const previous = current
    current = next
    if (previous === null) bindViewer(next)
    else retireViewer(queryClient, previous, next)
  })
}
